import { Component, ElementRef, input, signal, viewChild } from '@angular/core';
import { ContentAsset } from '../../../../core/models';

/**
 * Diaporama public (une image glisse vers la suivante) pour la galerie multimédia d'un contenu
 * (actualité, ressource...). Taille contenue en largeur (max-w-180) et ratio 16:9 fixe pour un
 * affichage net quelle que soit la taille d'origine des photos — ni minuscule, ni pleine largeur.
 * Défilement natif (scroll-snap) : pas de librairie, fonctionne au swipe, aux flèches et au clavier.
 */
@Component({
  selector: 'app-media-gallery-carousel',
  template: `
    @if (assets().length > 0) {
      <div class="mx-auto w-full max-w-180">
        <div class="relative">
          <div #track
            class="flex aspect-video w-full snap-x snap-mandatory overflow-x-auto scroll-smooth rounded-md border border-(--border-subtle) bg-black [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            (scroll)="onScroll()">
            @for (item of assets(); track item.id) {
              <div class="w-full shrink-0 snap-center">
                @if (item.mediaType === 'VIDEO') {
                  <video [src]="item.contentUrl || ''" [poster]="item.thumbnailUrl || ''" controls
                    class="h-full w-full object-contain"></video>
                } @else {
                  <img [src]="item.contentUrl || item.previewUrl || item.thumbnailUrl || ''" [alt]="item.altText || item.caption || ''"
                    class="h-full w-full object-cover" />
                }
              </div>
            }
          </div>

          @if (assets().length > 1) {
            <button type="button" (click)="go(activeIndex() - 1)" [disabled]="activeIndex() === 0"
              aria-label="Image précédente"
              class="absolute left-2 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-0 bg-black/55 text-white transition-opacity hover:bg-black/75 disabled:opacity-30">
              <i class="pi pi-chevron-left text-sm" aria-hidden="true"></i>
            </button>
            <button type="button" (click)="go(activeIndex() + 1)" [disabled]="activeIndex() === assets().length - 1"
              aria-label="Image suivante"
              class="absolute right-2 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-0 bg-black/55 text-white transition-opacity hover:bg-black/75 disabled:opacity-30">
              <i class="pi pi-chevron-right text-sm" aria-hidden="true"></i>
            </button>

            <div class="absolute bottom-2.5 left-1/2 flex -translate-x-1/2 gap-1.5" role="tablist" aria-label="Aller à l'image">
              @for (item of assets(); track item.id; let i = $index) {
                <button type="button" (click)="go(i)" role="tab" [attr.aria-selected]="i === activeIndex()"
                  [attr.aria-label]="'Image ' + (i + 1) + ' sur ' + assets().length"
                  class="h-2 cursor-pointer rounded-full border-0 p-0 transition-all"
                  [class.w-5]="i === activeIndex()" [class.bg-white]="i === activeIndex()"
                  [class.w-2]="i !== activeIndex()" [class.bg-white/50]="i !== activeIndex()"></button>
              }
            </div>
          }
        </div>

        @if (assets()[activeIndex()]?.caption; as caption) {
          <p class="m-0 mt-2 text-center text-[12.5px] text-(--text-muted)">{{ caption }}</p>
        }
      </div>
    }
  `,
})
export class MediaGalleryCarousel {
  readonly assets = input<ContentAsset[]>([]);

  private readonly trackRef = viewChild<ElementRef<HTMLDivElement>>('track');
  readonly activeIndex = signal(0);

  go(index: number): void {
    const total = this.assets().length;
    if (index < 0 || index >= total) return;
    const track = this.trackRef()?.nativeElement;
    if (!track) return;
    track.scrollTo({ left: track.clientWidth * index, behavior: 'smooth' });
    this.activeIndex.set(index);
  }

  onScroll(): void {
    const track = this.trackRef()?.nativeElement;
    if (!track || track.clientWidth === 0) return;
    const index = Math.round(track.scrollLeft / track.clientWidth);
    if (index !== this.activeIndex()) this.activeIndex.set(index);
  }
}
